import { Head, Link, router } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import FormSelect from '@/Components/FormSelect';
import Alert from '@/Components/Alert';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import { EyeIcon, TrashIcon, UsersIcon, MagnifyingGlassIcon, FunnelIcon, ArrowDownTrayIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

export default function Index({ customers, filters, flash }) {
    const [search, setSearch] = useState(filters?.search || '');
    const [status, setStatus] = useState(filters?.status || '');
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const applyFilters = (e) => { e?.preventDefault(); router.get('/customers', { search, status }, { preserveState: true }); };
    const clearFilters = () => { setSearch(''); setStatus(''); router.get('/customers'); };

    const statusBadge = (s) => {
        const map = { approved: 'success', pending: 'warning', rejected: 'danger' };
        return <Badge variant={map[s] || 'neutral'}>{s || 'Unknown'}</Badge>;
    };

    return (
        <AppLayout>
            <Head title="Customers" />
            <PageHeader title="Customers" subtitle="Manage credit facility customers" actions={
                <div className="flex items-center gap-2">
                    <Link href="/customer-invitations" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg transition-colors"><EnvelopeIcon className="w-4 h-4" /> Invitations</Link>
                    <a href="/customers/export-pdf" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-800 text-white rounded-lg transition-colors"><ArrowDownTrayIcon className="w-4 h-4" /> Export PDF</a>
                </div>
            } />
            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}

            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input type="text" placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20" />
                        </div>
                        <FormSelect value={status} onChange={e => setStatus(e.target.value)} className="w-full sm:w-40">
                            <option value="">All Status</option>
                            <option value="approved">Approved</option>
                            <option value="pending">Pending</option>
                            <option value="rejected">Rejected</option>
                        </FormSelect>
                        <div className="flex gap-2">
                            <Button type="submit" size="sm"><FunnelIcon className="w-4 h-4" /> Apply</Button>
                            <Button type="button" variant="outline" size="sm" onClick={clearFilters}>Clear</Button>
                        </div>
                    </form>
                </CardBody>
            </Card>

            <Card>
                <Table minWidth="800px">
                    <thead><tr><Th>Company</Th><Th>Contact</Th><Th>Status</Th><Th>Documents</Th><Th>Registered</Th><Th>Actions</Th></tr></thead>
                    <tbody>
                        {(Array.isArray(customers) ? customers : []).map(c => (
                            <Tr key={c.id}>
                                <Td className="font-medium text-slate-800 whitespace-nowrap">{c.company_name}</Td>
                                <Td><div><p className="text-sm text-slate-700">{c.contact_person_name}</p><p className="text-xs text-slate-400">{c.contact_person_email}</p></div></Td>
                                <Td>{statusBadge(c.status)}</Td>
                                <Td><Badge variant="info">{c.documents_count || c.documents?.length || 0}</Badge></Td>
                                <Td className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</Td>
                                <Td>
                                    <div className="flex items-center gap-1">
                                        <Link href={`/customers/${c.id}`} className="p-1.5 hover:bg-slate-100 rounded-lg"><EyeIcon className="w-4 h-4 text-slate-500" /></Link>
                                        <button onClick={() => setDeleteConfirm(c.id)} className="p-1.5 hover:bg-red-50 rounded-lg"><TrashIcon className="w-4 h-4 text-red-400" /></button>
                                    </div>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                {(!customers || customers.length === 0) && <EmptyState icon={UsersIcon} title="No customers found" description="Customers will appear here once registered." />}
            </Card>

            {/* Pagination placeholder - add when controller uses paginate() */}

            <Modal show={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Customer" iconBg="bg-red-100" iconColor="text-red-600" icon={<TrashIcon className="w-5 h-5" />}>
                <ModalBody><p className="text-sm text-slate-600">Are you sure?</p></ModalBody>
                <ModalFooter><Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button><Button variant="danger" onClick={() => { router.delete(`/customers/${deleteConfirm}`); setDeleteConfirm(null); }}>Delete</Button></ModalFooter>
            </Modal>
        </AppLayout>
    );
}
